// Колоните на „Картови плащания".
//
// Справката е сверката в края на деня: „С бон" трябва да е равно на отчета на
// заведението, а „С бон + Висящи" — на „край на деня" от самия терминал.
// Затова статусът е чип, а не текст: окото хваща висящите веднага.

import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import type { CardPaymentReportRow } from "../../../../api/reportsCardPayments";
import { EM_DASH, parseDecimal } from "../../../../components/dataTable";
import type {
  ChecklistOption,
  ChipCellValue,
  TextCellValue,
} from "../../../../components/dataTable";

/**
 * Ред с ВЕЧЕ РАЗРЕШЕНИ етикети. Преводите НЕ се вадят в `accessorFn`: TanStack
 * кешира стойността на клетката за живота на `data`, така че смяна на езика
 * иначе не стига до вече изчислените клетки.
 */
export type NamedCardPaymentRow = CardPaymentReportRow & {
  statusLabel: string;
  mountLabel: string;
  billLabel: string;
};

const columnHelper = createColumnHelper<NamedCardPaymentRow>();

/** Статусите идват като низ от FiscalAPI — непознат остава сив, не се крие. */
const STATUS_TONE: Record<string, "ok" | "danger" | "warn" | "neutral"> = {
  consumed: "ok",
  voided: "neutral",
  unknown: "danger",
  void_failed: "danger",
};

type Options = {
  statusOptions: ChecklistOption[];
  isMultiDay: boolean;
};

export const useCardPaymentsColumns = ({
  statusOptions,
  isMultiDay,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      columnHelper.accessor((row) => row.created_at, {
        id: "created_at",
        header: t("reportsPage.cardPaymentsColTime"),
        size: 104,
        meta: {
          cell: { kind: "dt", withDate: isMultiDay ? "always" : "never" },
          alwaysVisible: true,
          excel: { format: "date" },
        },
      }),

      columnHelper.accessor((row) => row.billLabel, {
        id: "bill",
        header: t("reportsPage.cardPaymentsColBill"),
        size: 112,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "text" },
          excel: { format: "text" },
        },
      }),

      columnHelper.accessor(
        (row): ChipCellValue => ({
          tone: STATUS_TONE[row.status] ?? "neutral",
          label: row.statusLabel,
        }),
        {
          id: "status",
          header: t("reportsPage.cardPaymentsColStatus"),
          size: 128,
          meta: {
            cell: { kind: "chip" },
            filter: { kind: "checklist", options: statusOptions },
            alwaysVisible: true,
          },
        },
      ),

      columnHelper.accessor(
        (row): TextCellValue => ({
          primary: row.masked_pan ?? "",
          secondary: row.rrn ? `RRN ${row.rrn}` : null,
        }),
        {
          id: "pan",
          header: t("reportsPage.cardPaymentsColPan"),
          size: 150,
          meta: {
            cell: { kind: "text", lines: 2 },
            hideBelow: "tablet",
            excel: { format: "text" },
          },
        },
      ),

      columnHelper.accessor((row) => row.auth_id ?? "", {
        id: "auth",
        header: t("reportsPage.cardPaymentsColAuth"),
        size: 104,
        meta: {
          cell: { kind: "text" },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      // Приета сума БЕЗ номер на документ е тихият признак на тази справка:
      // кръгче с контур на мястото на номера — същият знак както в „История на
      // маси", без нито една дума на екрана. Виждат го само сесии с втори код;
      // без него BE-то не връща такива редове изобщо.
      columnHelper.accessor((row) => row.receipt_number ?? "", {
        id: "receipt",
        header: t("reportsPage.cardPaymentsColReceipt"),
        size: 104,
        cell: (context) => {
          const receipt = context.getValue();
          if (receipt) {
            return (
              <span className="font-pos-mono text-[12.5px] font-semibold tabular-nums text-ink">
                {receipt}
              </span>
            );
          }
          if (context.row.original.status !== "consumed") {
            return <span className="text-faint">{EM_DASH}</span>;
          }
          return (
            <span className="flex items-center justify-end">
              <span
                aria-hidden
                className="size-[9px] shrink-0 rounded-full border-[1.5px] border-danger bg-transparent"
              />
              <span className="sr-only">
                {t("reportsPage.cardPaymentsColReceipt")}
              </span>
            </span>
          );
        },
        meta: {
          align: "right",
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      columnHelper.accessor((row) => row.mountLabel, {
        id: "mount",
        header: t("reportsPage.cardPaymentsColMount"),
        size: 104,
        meta: {
          cell: { kind: "text" },
          filter: { kind: "checklist" },
          hideBelow: "tablet",
          excel: { format: "text" },
        },
      }),

      columnHelper.accessor(
        (row): TextCellValue => ({
          primary: row.refund_rrn ?? "",
          secondary: row.refund_note ?? null,
        }),
        {
          id: "refund",
          header: t("reportsPage.cardPaymentsColRefund"),
          size: 150,
          meta: {
            cell: { kind: "text", lines: 2 },
            hideBelow: "tablet",
            excel: { format: "text" },
          },
        },
      ),

      columnHelper.accessor((row) => parseDecimal(row.amount), {
        id: "amount",
        header: t("reportsPage.cardPaymentsColAmount"),
        size: 116,
        meta: {
          cell: { kind: "money", strong: true },
          total: "sum",
          alwaysVisible: true,
          excel: { format: "money" },
        },
      }),
    ],
    [t, statusOptions, isMultiDay],
  );
};
